import * as React from "react";
import { useTranslation } from 'react-i18next';
import { Trans } from "react-i18next";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
} from "@mui/material";
import { Typography } from "@mui/material";
import {
  Delete as DeleteIcon,
  Security as SecurityIcon,
  PlayArrow as PlayArrowIcon,
  SettingsSuggest as SettingsSuggestIcon,
  FormatListBulleted as FormatListBulletedIcon,
  SaveAlt as SaveAltIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Alert from "@mui/material/Alert";
import AppContext from "../../utils/context";
import CustomJsonForm from "../../components/customform";
import { buildSchema } from "../../utils/schema";

export default function FormDialog(props) {
  const appContext = React.useContext(AppContext); 
  const { t } = useTranslation();
  const { events, database } = appContext;
  const [open, setOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({});
  const [formErrors, setFormErrors] = React.useState(null);
  const [instance, setInstance] = React.useState(null);
  const [isDrawer, setDrawer] = React.useState(false);
  const [project, setProject] = React.useState(null);
  const [components, setComponents] = React.useState([]);
  const [title, setTitle] = React.useState("");
  const [hasTriedToSave, setHasTriedToSave] = React.useState(false);
  const [schema, setSchema] = React.useState({});
  const [uischema, setUischema] = React.useState({});
  const [callback, setCallback] = React.useState(null);

  const helpInstancesMsg = t('instances.help.message', { defaultValue: undefined });
  const helpInstancesLink = t('instances.help.url', { defaultValue: undefined });

  React.useEffect(() => {
    const offCreate = events.on("create-instance", handleNew);
    const offEdit = events.on("edit-instance", handleEdit);

    return () => {
      offCreate();
      offEdit();
    };
  }, []);

  const handleNew = async (e) => {
    try {
      if (e?.callback) setCallback(() => e.callback);
      const { data: componentsData, error } = await database
        .from("components_project_templates")
        .select(
          `
          components(
            id,
            name,
            description,
            jsonschema,
            jsonschema_form,
            logo
          )
        `
        )
        .eq("project_template_id", e.project.project_template_id);

      if (error) throw error;

      setProject(e.project);
      setComponents(componentsData.map((component) => component.components));
      setDrawer(true);
    } catch (error) {
      events.dispatch("alert-message", {
        data: {
          message: t('instances.errors.loading_components'),
          severity: "error",
        },
      });
    }
  };

  const handleEdit = async (e) => {
    try {
      let data;
      if (e?.project) setProject(e.project);
      if (e?.callback) setCallback(() => e.callback);

      if (e.instance.id == null) {
        const { data: recordData, error } = await database
          .from("components")
          .select(
            `
            id,
            name,
            description,
            jsonschema,
            jsonschema_form,
            logo
          `
          )
          .eq("id", e.instance.components.id)
          .single();

        if (error) throw error;

        data = {
          id: null,
          components: recordData,
          metadata: {},
        };
      } else {
        const { data: recordData, error } = await database
          .from("instances")
          .select(
            `
            id,
            metadata,
            components(
              id,
              name,
              description,
              jsonschema,
              jsonschema_form,
              logo
            )
          `
          )
          .eq("id", e.instance.id)
          .single();

        if (error) throw error;

        data = recordData;
      }

      const r = await buildSchema(
        database,
        data.components.jsonschema, {
        workspace_id: e?.project?.workspace_id,
        project_template_id: e?.project?.project_template_id,
        project_id: e?.project?.id,
        instance_id: data?.id,
      })

      setInstance(data);
      setSchema(r);
      setUischema(data.components.jsonschema_form);
      setTitle(
        e.instance.id != null
          ? t('instances.save.existing')
          : t('instances.save.new') + ': ' + t(e.instance.components.name)
      );
      setFormData({ ...data.metadata });
      setOpen(true);
    } catch (error) {
      events.dispatch("alert-message", {
        data: {
          message: t('instances.errors.loading_instances'),
          severity: "error",
        },
      });
    }
  };

  const save = async () => {
    setHasTriedToSave(true);

    if (formErrors && formErrors.length) {
      events.dispatch("alert-message", {
        data: { message: t('commons.errors.fix_errors'), severity: "error" },
      });
      return;
    }
    const postData = {
      component_id: instance.components.id,
      project_id: project.id,
      metadata: formData,
    }
    try {
      if (instance.id == null) {
        const { data, error } = await database
          .from("instances")
          .insert(postData)
          .select();

        if (error) throw error;
        if (callback) callback(data);
      } else {
        const { data, error } = await database
          .from("instances")
          .update(postData)
          .eq("id", instance.id)
          .select();

        if (error) throw error;
        if (callback) callback(data);
      }

      handleClose();
    } catch (error) {
      console.error(error);
      events.dispatch("alert-message", {
        data: {
          message: t('instances.save.error'),
          severity: "error",
        },
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setDrawer(false);
    setFormData({});
    setFormErrors(null);
    setSchema({});
    setUischema({});
    setTitle("");
    setComponents([]);
    setProject(null);
    setInstance(null);
    setHasTriedToSave(false);
    setCallback(null);
  };

  const addInstance = (component) => {
    handleEdit({
      instance: { id: null, components: component },
      project,
    });
  };

  
  const setFormResult = ({ data, errors }) => {
    setFormData(data);
    setFormErrors(errors);
  };


  return (
    <React.Fragment>
      <Drawer
        open={isDrawer}
        anchor="right"
        sx={{ overflow: "none" }}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setDrawer(false);
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Typography>
            {t('instances.title')} : {project && project.name}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setDrawer(false)}>{t('buttons.close')}</Button>
        </DialogTitle>
        <DialogContent
          sx={{
            width: {
              xs: "90vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            }
          }}
        >
          {helpInstancesMsg &&
            <Alert severity="info" onClose={() => { }}
              action=
              {helpInstancesLink && <Button color="inherit" size="small" onClick={() => {
                window.open(helpInstancesLink, "_blank");
              }}>
                {t('buttons.help')}
              </Button>}
            >
              {helpInstancesMsg}
            </Alert>}

          <List sx={{ width: "100%" }}>
            {components.map((component) => (
              <ListItem
                key={component.id}
                secondaryAction={
                  <Button onClick={() => addInstance(component)}>{t('buttons.add')}</Button>
                }
              >
                <ListItemIcon>
                  <img
                    src={component.logo}
                    style={{ width: 32, height: 32, marginRight: 10 }}
                  />
                </ListItemIcon>
                <ListItemText  primary={t(component.name)} secondary={t(component.description)} />

              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Drawer>

      <Drawer
        open={open}
        anchor="right"
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Typography className="m-0 p-0">{title}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button
              onClick={save}
              startIcon={<SaveAltIcon />}
              variant="outlined"
              color="success"
            >
              {t('buttons.save')}
            </Button>
            <Button onClick={handleClose}>{t('buttons.cancel')}</Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{
          width: {
            xs: "90vw",
            sm: "80vw",
            md: "70vw",
            lg: "60vw",
            xl: "50vw",
          }
        }}>
          {schema && project && (
            <Box className="jsonforms" style={{ width: "100%" }}>
              <CustomJsonForm
                schema={schema}
                uischema={uischema}
                data={{ ...formData }}
                contextVars={{
                  workspace_id: project?.workspace_id,
                  project_template_id: project?.project_template_id,
                  project_id: project?.id,
                  instance_id: instance?.id,
                }}
                onChange={setFormResult}
                validationMode={hasTriedToSave ? 'ValidateAndShow' : 'ValidateAndHide'}
                config={{ showUnfocusedDescription: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", padding: "20px" }}>
          <small>
            <Trans
              i18nKey="configurations.helper_secrets"
              components={{ code: <code /> }}
              values={{}} // pas de variable
            />
          </small>
        </DialogActions>
      </Drawer>
    </React.Fragment>
  );
}
